/* Small building blocks for the compact Payments page, on top of the shared compact kit. */

import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { Icon } from '../ChatAssistant/compact/kit';
import { initials } from './model';
import { ROW_LINE, TH } from './styles';
import './payments.css';


/** 10px uppercase caption used above figures and lists. */
export function Caption({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span style={{ ...TH, ...style }}>{children}</span>;
}

/** "Title  hint" heading row with an optional right side. */
export function Head({ title, hint, children }: { title: ReactNode; hint?: ReactNode; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ font: f(600, 13), color: C.text }}>{title}</span>
      {hint && <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>}
      {children}
    </div>
  );
}

export function Avatar({ name, src, size = 28 }: { name: string; src?: string | null; size?: number }) {
  if (src) return <img src={src} alt="" style={{ width: size, height: size, flex: 'none', borderRadius: 99, objectFit: 'cover' }} />;
  return (
    <span style={{ width: size, height: size, flex: 'none', borderRadius: 99, background: C.segActive, color: C.purplePale, font: f(600, size > 26 ? 11 : 10.5), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {initials(name)}
    </span>
  );
}

/** A strip of figures separated by 1px lines (stats on AI Usage and Referral program). */
export function StatStrip({ items, min = 170 }: { items: { label: string; value: string; sub?: string; accent?: boolean }[]; min?: number }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`, gap: 1, background: C.border, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
      {items.map((s) => (
        <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '12px 16px', background: C.card }}>
          <Caption>{s.label}</Caption>
          <span style={{ font: f(700, 18), color: s.accent ? C.purplePale : C.text, fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
          <span style={{ font: f(400, 11), color: C.muted }}>{s.sub || ' '}</span>
        </div>
      ))}
    </div>
  );
}

/** Scrollable table shell: a card that scrolls sideways below `minWidth`. */
export function Table({ minWidth, children }: { minWidth: number; children: ReactNode }) {
  return (
    <div className="pay-scroll" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflowX: 'auto' }}>
      <div style={{ minWidth }}>{children}</div>
    </div>
  );
}

export function THeadRow({ cols, children, height = 34 }: { cols: string; children: ReactNode; height?: number }) {
  return (
    <div className="pay-thead" style={{ ...TH, display: 'grid', gridTemplateColumns: cols, gap: 12, alignItems: 'center', height, padding: '0 14px', borderBottom: ROW_LINE, whiteSpace: 'nowrap' }}>{children}</div>
  );
}

/** Bordered pill-shaped button used for secondary actions (30px). */
export function GhostBtn({ icon, children, onClick, disabled, busy, title, height = 30, style }: { icon?: string; children: ReactNode; onClick?: () => void; disabled?: boolean; busy?: boolean; title?: string; height?: number; style?: CSSProperties }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`cpt-hover-border cpt-hover-text${busy ? ' om-busy' : ''}`}
      style={{ height, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: `1px solid ${C.borderHi}`, borderRadius: 6, color: 'var(--om-body)', font: f(500, 12), cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1, flex: 'none', ...style }}
    >
      {icon && <Icon name={icon} size={12} />}
      {children}
    </button>
  );
}

export function PrimaryBtn({ icon, iconRight, children, onClick, disabled, title, height = 32, style, className }: { icon?: string; iconRight?: string; children: ReactNode; onClick?: () => void; disabled?: boolean; title?: string; height?: number; style?: CSSProperties; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={'pay-primary' + (className ? ' ' + className : '')}
      style={{ height, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 7, border: 0, borderRadius: 7, background: C.purple, color: '#fff', font: f(600, 13), cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap', opacity: disabled ? 0.45 : 1, flex: 'none', ...style }}
    >
      {icon && <Icon name={icon} size={13} />}
      {children}
      {iconRight && <Icon name={iconRight} size={13} />}
    </button>
  );
}

export function TextBtn({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className="cpt-hover-text" style={{ height: 32, padding: '0 11px', border: 0, borderRadius: 7, background: 'transparent', color: C.sub, font: f(500, 12.5), cursor: 'pointer', flex: 'none' }}>
      {children}
    </button>
  );
}

/** Square 16px checkbox. */
export function Check({ on, onClick, disabled, title }: { on: boolean; onClick: () => void; disabled?: boolean; title?: string }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      title={title}
      onClick={() => !disabled && onClick()}
      style={{ width: 16, height: 16, padding: 0, boxSizing: 'border-box', borderRadius: 4, border: `1px solid ${on ? C.purple : C.faint}`, background: on ? C.purple : 'transparent', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.3 : 1 }}
    >
      {on && <Icon name="check" size={11} />}
    </button>
  );
}

export function Dash({ title }: { title?: string }) {
  return <span title={title} style={{ font: f(400, 12), color: C.faint }}>—</span>;
}

export function Note({ icon = 'info', children, tone = 'muted', style }: { icon?: string; children: ReactNode; tone?: 'muted' | 'accent'; style?: CSSProperties }) {
  return (
    <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6, font: f(400, 11.5, 1.45), color: tone === 'accent' ? C.purplePale : C.sub, ...style }}>
      <Icon name={icon} size={12} style={{ marginTop: 2 }} />
      <span>{children}</span>
    </span>
  );
}

/* ── Modal ──────────────────────────────────────────────────────── */
export function Modal({ width, onClose, children, closeOnBackdrop = true }: { width: number; onClose: () => void; children: ReactNode; closeOnBackdrop?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div
      role="presentation"
      onClick={closeOnBackdrop ? onClose : undefined}
      className="cpt-root"
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="pay-thin-scroll"
        style={{ width, maxWidth: '100%', maxHeight: 'calc(92 * var(--om-vh))', overflowY: 'auto', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.6)', display: 'flex', flexDirection: 'column' }}
      >
        {children}
      </div>
    </div>
  );
}

export function ModalHead({ icon, title, right, onClose, pad = '14px 16px 4px' }: { icon: string; title: ReactNode; right?: ReactNode; onClose: () => void; pad?: string }) {
  const { t } = useTranslation('paymentsCompact');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: pad }}>
      <Icon name={icon} size={15} color={C.purpleLt} />
      <span style={{ flex: 1, font: f(600, 15), color: C.text }}>{title}</span>
      {right}
      <button type="button" aria-label={t('close')} onClick={onClose} className="cpt-hover-icon" style={{ width: 28, height: 28, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="x" size={15} />
      </button>
    </div>
  );
}

export function ModalFoot({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderTop: `1px solid ${C.border}`, ...style }}>{children}</div>;
}

export interface ConfirmSpec {
  title: string;
  body: ReactNode;
  confirm: string;
  icon?: string;
  run: () => void | Promise<void>;
}

export function ConfirmDialog({ spec, onClose }: { spec: ConfirmSpec; onClose: () => void }) {
  const { t } = useTranslation('paymentsCompact');
  return (
    <Modal width={420} onClose={onClose}>
      <ModalHead icon={spec.icon ?? 'alert-circle'} title={spec.title} onClose={onClose} />
      <div style={{ padding: '4px 16px 16px', font: f(400, 12.5, 1.5), color: C.sub }}>{spec.body}</div>
      <ModalFoot>
        <span style={{ flex: 1 }} />
        <TextBtn onClick={onClose}>{t('cancel')}</TextBtn>
        <PrimaryBtn height={30} onClick={() => { onClose(); void spec.run(); }}>{spec.confirm}</PrimaryBtn>
      </ModalFoot>
    </Modal>
  );
}
